import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getUserBookings, cancelBooking } from '../services/api';
import {
  TreePine, User, Mail, Phone, Calendar, MapPin, LogOut,
  Edit2, X, Check, AlertCircle, Clock, DollarSign, Loader2
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateProfile, changePassword, isAuthenticated, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', phone: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || '', phone: user.phone || '' });
      fetchBookings();
    }
  }, [user]);

  const fetchBookings = async () => {
    try {
      const data = await getUserBookings();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const result = await updateProfile(profileData);
    if (result.success) {
      setEditingProfile(false);
      setSuccess('Profile updated successfully');
    } else {
      setError(result.error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new !== passwordData.confirm) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.new.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const result = await changePassword(passwordData.current, passwordData.new);
    if (result.success) {
      setChangingPassword(false);
      setPasswordData({ current: '', new: '', confirm: '' });
      setSuccess('Password changed successfully');
    } else {
      setError(result.error);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await cancelBooking(bookingId);
      fetchBookings();
      setSuccess('Booking cancelled successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBookingStatus = (booking) => {
    const checkIn = new Date(booking.check_in);
    const checkOut = new Date(booking.check_out);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (booking.status === 'cancelled') return { label: 'Cancelled', color: 'text-red-400 bg-red-500/10' };
    if (checkOut < today) return { label: 'Completed', color: 'text-stone-400 bg-stone-500/10' };
    if (checkIn <= today && checkOut >= today) return { label: 'In Progress', color: 'text-emerald-400 bg-emerald-500/10' };
    return { label: 'Upcoming', color: 'text-blue-400 bg-blue-500/10' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const upcomingBookings = bookings.filter(b => {
    const checkIn = new Date(b.check_in);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return checkIn >= today && b.status !== 'cancelled';
  });

  const pastBookings = bookings.filter(b => {
    const checkOut = new Date(b.check_out);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return checkOut < today || b.status === 'cancelled';
  });

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TreePine className="w-8 h-8 text-emerald-400" />
            <span className="text-xl font-semibold text-white">
              Stay <span className="text-emerald-400">Tran-Quil</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/book">
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-6">
                Book a Stay
              </Button>
            </Link>
            <button
              onClick={handleLogout}
              className="text-stone-400 hover:text-white transition-colors p-2"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back, {user.name?.split(' ')[0]}!</h1>
          <p className="text-stone-400">Manage your bookings and profile</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-400">{success}</p>
            <button onClick={() => setSuccess('')} className="ml-auto text-emerald-400 hover:text-emerald-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Section */}
          <div className="lg:col-span-1">
            <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Profile</h2>
                {!editingProfile && (
                  <button
                    onClick={() => setEditingProfile(true)}
                    className="text-emerald-400 hover:text-emerald-300 p-1"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Profile Picture */}
              <div className="flex justify-center mb-6">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <User className="w-10 h-10 text-emerald-400" />
                  </div>
                )}
              </div>

              {editingProfile ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <Label className="text-stone-400 text-sm">Name</Label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="bg-stone-900/50 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-400 text-sm">Phone</Label>
                    <Input
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="bg-stone-900/50 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white">
                      Save
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setEditingProfile(false);
                        setProfileData({ name: user.name || '', phone: user.phone || '' });
                      }}
                      className="bg-stone-700 hover:bg-stone-600 text-white"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-stone-300">
                    <User className="w-5 h-5 text-stone-500" />
                    <span>{user.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-300">
                    <Mail className="w-5 h-5 text-stone-500" />
                    <span>{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-3 text-stone-300">
                      <Phone className="w-5 h-5 text-stone-500" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  <div className="pt-4 border-t border-stone-700">
                    <p className="text-xs text-stone-500 mb-1">Signed in with</p>
                    <p className="text-stone-400 capitalize">{user.auth_provider}</p>
                  </div>
                </div>
              )}

              {/* Change Password */}
              {user.auth_provider === 'email' && (
                <div className="mt-6 pt-6 border-t border-stone-700">
                  {changingPassword ? (
                    <form onSubmit={handleChangePassword} className="space-y-3">
                      <Input
                        type="password"
                        placeholder="Current password"
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                        className="bg-stone-900/50 border-stone-600 text-white"
                      />
                      <Input
                        type="password"
                        placeholder="New password"
                        value={passwordData.new}
                        onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                        className="bg-stone-900/50 border-stone-600 text-white"
                      />
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordData.confirm}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                        className="bg-stone-900/50 border-stone-600 text-white"
                      />
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm">
                          Update
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            setChangingPassword(false);
                            setPasswordData({ current: '', new: '', confirm: '' });
                          }}
                          className="bg-stone-700 hover:bg-stone-600 text-white text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <button
                      onClick={() => setChangingPassword(true)}
                      className="text-sm text-emerald-400 hover:text-emerald-300"
                    >
                      Change Password
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bookings Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Bookings */}
            <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Upcoming Bookings</h2>
              
              {loadingBookings ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400 mb-4">No upcoming bookings</p>
                  <Link to="/book">
                    <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full">
                      Book Your Stay
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map((booking) => {
                    const status = getBookingStatus(booking);
                    return (
                      <div
                        key={booking.booking_id}
                        className="bg-stone-900/50 border border-stone-700/50 rounded-xl p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                          <div>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-emerald-400">
                              ${booking.total_price.toFixed(2)}
                            </div>
                            <div className="text-xs text-stone-500">{booking.nights} nights</div>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-stone-500 mb-1">Check-in</p>
                            <p className="text-white font-medium">{formatDate(booking.check_in)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-stone-500 mb-1">Check-out</p>
                            <p className="text-white font-medium">{formatDate(booking.check_out)}</p>
                          </div>
                        </div>
                        {booking.status !== 'cancelled' && new Date(booking.check_in) > new Date() && (
                          <button
                            onClick={() => handleCancelBooking(booking.booking_id)}
                            className="mt-4 text-sm text-red-400 hover:text-red-300"
                          >
                            Cancel Booking
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Past Bookings */}
            {pastBookings.length > 0 && (
              <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6">Past Bookings</h2>
                <div className="space-y-3">
                  {pastBookings.map((booking) => {
                    const status = getBookingStatus(booking);
                    return (
                      <div
                        key={booking.booking_id}
                        className="bg-stone-900/50 border border-stone-700/50 rounded-xl p-4 opacity-70"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${status.color}`}>
                              {status.label}
                            </span>
                            <span className="text-stone-400">
                              {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                            </span>
                          </div>
                          <span className="text-stone-400">${booking.total_price.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
